<svelte:options tag={null} />

<script lang="ts">
  import { EventHandler } from '../../utilities/EventHandler';
  import Breadcrumb from '../breadcrumb/Breadcrumb.nested.svelte';
  import type { BreadcrumbItems } from '../breadcrumb/breadcrumb.types';
  import Tabs from '../tabs/Tabs.nested.svelte';
  import type { TabsItems } from '../tabs/tabs.types';
  import Badge from '../badge/Badge.nested.svelte';
  import type { BadgeType } from '../badge/badge.types';
  import type { A11yLabels, HeaderSize } from './Header.types';

  export let title: string;
  export let subtitle: string;
  export let size: HeaderSize = 'm';
  export let status: string;
  export let statustype: BadgeType;
  export let back: boolean | string;
  export let breadcrumb: string | BreadcrumbItems[];
  export let tabs: string | TabsItems[];
  export let noshadow: boolean | string;
  export let a11ylabels: string | A11yLabels;
  export let scrollcontainer: HTMLElement;

  export let hasicons: boolean;
  export let hasselector: boolean;
  export let hideoptions = false;

  const headerAnimationThreshold = 5;

  let eventHandler = new EventHandler();
  let originalScrollPos = 0;
  let hasAnimation: boolean;
  let root: HTMLElement;

  $: parsedBreadcrumb =
    typeof breadcrumb === 'string'
      ? (JSON.parse(breadcrumb) as BreadcrumbItems[])
      : breadcrumb;

  $: parsedTabs =
    typeof tabs === 'string' ? (JSON.parse(tabs) as TabsItems[]) : tabs;

  $: parsedA11yLabels =
    typeof a11ylabels === 'string'
      ? (JSON.parse(a11ylabels) as A11yLabels)
      : a11ylabels;

  $: hasBack = back && back !== 'false';
  $: hasNoShadow = noshadow && noshadow !== 'false';
  $: {
    hasAnimation = Boolean(scrollcontainer);
    updateAnimation(scrollcontainer);
  }
  $: attributes = { ...$$restProps };
  $: delete attributes.class;
  function goBack() {
    eventHandler.dispatch('on-back-click', {});
  }

  function updateAnimation(element: HTMLElement) {
    if (element) {
      element.removeEventListener('scroll', handleScroll);
      element.addEventListener('scroll', handleScroll);
    }
  }

  function handleScroll() {
    const currentScrollPos = scrollcontainer.scrollTop;
    const originHeaderMargin = Number(root.style.marginTop.replace('px', ''));

    if (originalScrollPos > currentScrollPos) {
      // scroll up
      if (currentScrollPos < root.offsetHeight) {
        // scroll while in header zone
        root.style.marginTop = `${Math.max(
          -currentScrollPos,
          originHeaderMargin,
        )}px`;
      } else {
        // scroll while below header zone
        root.style.marginTop = '0';
      }
    } else {
      // scroll down
      if (currentScrollPos < root.offsetHeight) {
        // scroll while in header zone

        root.style.transition = 'none';
        root.style.marginTop = `${-currentScrollPos}px`;
      } else {
        // scroll while below header zone
        root.style.transition = 'margin-top ease-out 0.4s';
        root.style.marginTop = `${
          -root.offsetHeight - headerAnimationThreshold
        }px`;
      }
    }

    originalScrollPos = currentScrollPos;
  }
</script>

<div
  class="mc-header {size ? `mc-header--${size}` : ''}"
  class:mc-header--no-shadow={hasNoShadow}
  class:mc-header--animate={hasAnimation}
  bind:this={root}
  {...attributes}
>
  <div class="mc-header__left">
    {#if breadcrumb}
      <div class="mc-header__breadcrumb">
        <Breadcrumb
          items={JSON.stringify(parsedBreadcrumb)}
          nopadding="true"
          on:link-clicked={(event) =>
            eventHandler.dispatch('link-clicked', event.detail)}
        />
      </div>
    {/if}

    {#if hasBack}
      <button class="mc-header__back" on:click={goBack}>
        <span class="mc-header__back__label">
          {parsedA11yLabels?.['back'] || 'Back'}
        </span>
      </button>
    {/if}

    <div class="mc-header__heading">
      <h1 class="mc-header__heading__title">
        {title}
      </h1>
      {#if subtitle}
        <div class="mc-header__heading__subtitle">
          {subtitle}
        </div>
      {/if}
      {#if status}
        <div class="mc-header__heading__status">
          <Badge label={status} type={statustype} />
        </div>
      {/if}
    </div>
  </div>
  {#if !hideoptions}
    <div class="mc-header__right">
      {#if $$slots?.icons || hasicons}
        <div class="mc-header__icons">
          <slot name="icons" />
        </div>
      {/if}
      {#if $$slots?.selector || hasselector}
        <div
          class:mc-header__selector--show-separator={($$slots?.icons ||
            hasicons) &&
            ($$slots?.selector || hasselector)}
          class="mc-header__selector"
        >
          <slot name="selector" />
        </div>
      {/if}
    </div>
  {/if}
  {#if parsedTabs}
    <div class="mc-header__tabs">
      <Tabs
        tabs={JSON.stringify(parsedTabs)}
        nodivider={true}
        on:tab-clicked={(event) =>
          eventHandler.dispatch('tab-clicked', event.detail)}
      />
    </div>
  {/if}
</div>

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';
  @import './_c.header.scss';

  :host {
    display: block;
    line-height: 0;
  }
</style>
